import React, { useCallback, useEffect, useMemo } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import useAction from '../../../hooks/useAction'
import FilterCollapse from '../../../components/FilterCollapse'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import { Box } from '@mui/material'
import RowStatus from '../../../components/RowStatus'
import {
  EquipmentEnum,
  EquipmentEnumPtBRLabelById,
} from '../../../enums/equipment-enum'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { equipmentsStatusColorMap } from '../../../utils/statusColorMap'
import AlertMessage from '../../../components/AlertMessage'

const FILTER_FIELD_CONFIG = {
  number: 'value',
  'address.addressable_id': 'value',
  address_id: 'value',
}

const EquipmentPage = () => {
  const { canAccess } = usePermissions()

  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      'organization.name': [],
      number: [],
      'productItem.product.type.name': [],
      status_id: [],
      name: [],
      'address.addressable_id': [],
      address_id: [],
      deleted_at: false,
    },
    'equipments'
  )

  const { handleFilterClear } = useFilterHandlers(
    applyUrlFilters,
    FILTER_FIELD_CONFIG
  )

  const handleFilterApply = useCallback(
    (currentValues) => {
      const transformedFilters = transformFiltersForUrl(
        currentValues,
        FILTER_FIELD_CONFIG
      )

      // O cliente do filtro é o dono do endereço atual do equipamento:
      // equipments.address_id → addresses.addressable (customer | organization).
      // O type restringe a cliente, pois o addressable é polimórfico.
      if (transformedFilters['address.addressable_id']) {
        transformedFilters['address.addressable_type'] = { $eq: 'customer' }
      }

      applyUrlFilters(transformedFilters)
    },
    [applyUrlFilters]
  )

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        'organization.name': [],
        number: [],
        'productItem.product.type.name': [],
        status_id: [],
        name: [],
        'address.addressable_id': [],
        address_id: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const mapStatus = useCallback((statusData) => {
    return Object.fromEntries(
      statusData.map((status) => [status.id, status.name])
    )
  }, [])

  const { data: statusData, isLoading: isLoadingStatus } = useQueryWithLoader({
    queryKey: ['status'],
    queryFn: async () => {
      const response = await baseServiceGet(entities.status.get)
      return response.result
    },
    staleTime: Infinity,
  })

  const statusMap = useMemo(() => {
    if (!statusData) return {}
    return mapStatus(statusData.data)
  }, [statusData, mapStatus])

  const excludedEquipmentStatusIds = useMemo(
    () =>
      (statusData?.data || [])
        .filter((status) => ['EMPRESTADO', 'LIBERADO'].includes(status.name))
        .map((status) => status.id),
    [statusData]
  )

  const {
    data: equipmentsData,
    isLoading: isLoadingEquipments,
    isFetching: isFetchingEquipments,
    isError: isErrorEquipments,
    error: equipmentsError,
  } = useQueryWithLoader({
    queryKey: ['equipments', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        is_completed: true,
        deleted_at: false,
        ...filters,
      })

      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'number',
          order: 'desc',
        },
      }

      const response = await baseServiceGet(
        entities.equipments.allCleaned.get,
        params
      )
      return response.result
    },
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    onlyFirstLoad: true,
    select: (data) => {
      return {
        equipments: data.data?.map((equipment) => ({
          id: equipment.id,
          name: equipment.name,
          number: equipment.number,
          status: EquipmentEnumPtBRLabelById[equipment?.status_id] || '-',
          status_id: equipment.status_id,
          current_customer:
            [EquipmentEnum.RESERVED, EquipmentEnum.RENTED].includes(Number(equipment.status_id))
              ? equipment.current_customer?.alias ?? '-'
              : '-',
          equipment_address:
            equipment.status_id === EquipmentEnum.RESERVED
              ? equipment.address?.formatted_address ?? '-'
              : equipment.current_address?.formatted_address ?? '-',
          isDeleted: !!equipment.deleted_at,
        })),
        totalPages: data.last_page,
      }
    },
    enabled: !!statusMap,
    placeholderData: (previousData) => previousData,
  })

  const { equipments = [], totalPages = 1 } = equipmentsData || {}

  const columns = [
    { key: 'number', label: 'Tombo' },
    { key: 'name', label: 'Descrição', width: '25%' },
    { key: 'status', label: 'Status' },
    { key: 'current_customer', label: 'Cliente', width: '25%' },
    { key: 'equipment_address', label: 'Endereço atual' },
  ]

  useEffect(() => {
    if (isLoadingStatus) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoadingStatus, showLoader, hideLoader])

  useEffect(() => {
    if (isErrorEquipments) {
      console.error(
        'Erro ao carregar equipamentos (useQuery):',
        equipmentsError
      )
      showNotification(
        'error',
        `Erro ao carregar equipamentos: ${
          equipmentsError.response?.data?.message || equipmentsError.message
        }`
      )
    }
  }, [isErrorEquipments, equipmentsError, showNotification])

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries(['equipments'])
  }, [queryClient])

  const {
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const inactivateEquipmentMutation = useMutation({
    mutationFn: async (productId) => {
      showLoader()
      const response = await baseServiceDelete(
        entities.equipments.delete(productId)
      )
      return response.result
    },
    onSuccess: () => {
      refreshList()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      handleCancelConfirmation()
    },
  })

  const activateEquipmentMutation = inactivateEquipmentMutation

  const actions = [
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver equipamentos',
      onClick: (equipment) =>
        navigate(`/aluguel/equipamentos/detalhes/${equipment.id}`),
      show: (equipment) =>
        !equipment.isDeleted && canAccess('Ver equipamentos'),
    },
    {
      id: 'edit',
      icon: faEdit,
      title: 'Editar',
      buttonClass: 'btn-primary',
      permission: 'Atualizar equipamentos',
      onClick: (equipment) =>
        navigate(`/aluguel/equipamentos/editar/${equipment.id}`),
      show: (equipment) =>
        !equipment.isDeleted && canAccess('Atualizar equipamentos'),
    },
    {
      id: 'delete',
      icon: faTrash,
      title: 'Excluir',
      buttonClass: 'btn-danger',
      permission: 'Excluir equipamentos',
      onClick: (equipment) =>
        showConfirmation(
          equipment,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactivateEquipmentMutation.mutateAsync(equipment.id)
            } catch (error) {
              console.error('Erro ao excluir equipamentos:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (equipment) =>
        !equipment.isDeleted &&
        equipment.status_id === EquipmentEnum.AVAILABLE &&
        canAccess('Excluir equipamentos'),
    },
    {
      id: 'activate',
      icon: faUndo,
      title: 'Restaurar',
      buttonClass: 'btn-info',
      permission: 'Excluir equipamentos',
      onClick: (equipment) =>
        showConfirmation(
          equipment,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateEquipmentMutation.mutateAsync(equipment.id)
            } catch (error) {
              console.error('Erro ao restaurar equipamentos:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (equipment) =>
        equipment.isDeleted && canAccess('Excluir equipamentos'),
    },
  ]

  const inputsfilters = [
    {
      label: 'Equipamento',
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase={['number', 'name']}
          extraFilters={{ is_completed: { $eq: 1 } }}
          model="equipment"
          value={localFilterValues.number}
          valueKey="number"
          onChange={(selected) => handleFilterChange('number', selected)}
          placeholder="Selecione ou digite o numero ou a descrição"
          isMulti
        />
      ),
    },
    {
      label: 'Tipo',
      component: (
        <AutoCompleteFilter
          service={baseService}
          model="type"
          columnDataBase="name"
          extraFilters={{ 'products.is_asset': { $eq: 1 } }}
          value={localFilterValues['productItem.product.type.name']}
          onChange={(selected) =>
            handleFilterChange('productItem.product.type.name', selected)
          }
          placeholder="Selecione ou digite o tipo"
          isMulti
        />
      ),
    },
    {
      label: 'Situações',
      gridSize: '9',
      component: (
        <AutoCompleteFilter
          service={baseService}
          model="status"
          columnDataBase="name"
          extraFilters={{ id: { $notIn: excludedEquipmentStatusIds } }}
          value={localFilterValues.status_id}
          onChange={(selected) => handleFilterChange('status_id', selected)}
          placeholder="Selecione os status"
          isMulti
        />
      ),
    },
    {
      label: 'Cliente',
      component: (
        <AutoCompleteFilter
          service={baseService}
          model="customer"
          columnDataBase={['id', 'alias']}
          value={localFilterValues['address.addressable_id']}
          valueKey="id"
          onChange={(selected) =>
            handleFilterChange('address.addressable_id', selected)
          }
          placeholder="Selecione ou digite o cliente"
        />
      ),
    },
    {
      label: 'Destino',
      component: (
        <AutoCompleteFilter
          service={baseService}
          model="address"
          columnDataBase={['alias', 'street', 'city']}
          value={localFilterValues.address_id}
          valueKey="id"
          onChange={(selected) =>
            handleFilterChange('address_id', selected)
          }
          placeholder="Selecione ou digite o endereço de destino"
          isMulti
        />
      ),
    },
  ]

  const {
    data: equipmentData,
    isLoading: equipmentDataLoading,
    isFetching: equipmentDataFetching,
  } = useQuery({
    queryKey: ['equipmentOptions'],
    queryFn: async () => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        is_completed: false,
        status_id: 1,
      })

      const params = { filters, perPage: 99999 }
      const res = await baseServiceGet(entities.equipments.get, params)
      return res.result.data
    },
    refetchOnWindowFocus: true,
  })

  const hasPendingEquipments = equipmentData?.length > 0
  const pendingCount = equipmentData?.length || 0

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Equipamentos"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <Box
          sx={{
            mt: 4,
          }}>
          <RowStatus />
        </Box>

        <ListHeader
          title="Lista de Equipamentos"
          buttonText="Tombar Equipamento"
          buttonLink="/aluguel/equipamentos/criar"
          permission="Criar equipamentos"
          canAccess={canAccess}
        />

        <AlertMessage
          isLoading={equipmentDataLoading || equipmentDataFetching}
          condition={hasPendingEquipments}
          warningMessage={
            <span>
              Existe(m){' '}
              <strong style={{ fontSize: '1rem' }}>{pendingCount}</strong>{' '}
              equipamento(s) pendente(s) para tombar. Clique em "Tombar
              Equipamento" para completar o cadastro.
            </span>
          }
          successMessage="Não há equipamentos para tombar no momento."
        />

        <TableWithInactiveFilter
          data={equipments}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          rowColorConfig={{
            colorMap: equipmentsStatusColorMap,
            propertyKey: 'status_id',
          }}
          showFilterInactive
          isLoading={isLoadingEquipments || isLoadingStatus}
          isFetching={isFetchingEquipments && !isLoadingEquipments}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ''}
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default EquipmentPage
